import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  LuAlarmClock,
  LuBookOpen,
  LuBookmark,
  LuChevronRight,
  LuCompass,
  LuEye,
  LuFlame,
  LuGraduationCap,
  LuCalendarDays,
  LuClock,
  LuLayoutGrid,
  LuLibrary,
  LuListChecks,
  LuMap,
  LuMic,
  LuPlay,
  LuRefreshCw,
  LuSparkles,
  LuMessageCircle,
  LuSettings,
  LuTarget,
  LuTrendingUp,
  LuTrophy,
  LuUser,
  LuWandSparkles,
  LuZap,
} from 'react-icons/lu';
import AuthContext from '../shared/AuthContext';
import { getDailyReviewGoal } from '../../utils/learningPreferences';
import { formatLastSeen } from '../../utils/formatLastSeen';
import LearningGuide from '../learning/LearningGuide';
import { STUDY_HUB } from '../../config/appLabels';
import StudyHubAnalytics from '../analytics/StudyHubAnalytics';
import LearningProgressPanel from '../gamification/LearningProgressPanel';
import useGamificationSummary from '../../hooks/useGamificationSummary';
import { REALTIME_INVALIDATE_EVENT } from '../../realtime/realtimeBus';
import { useLearningRealtime } from '../../realtime/LearningRealtimeProvider';
import '../navigation/appNav.css';
import '../learning/LearningLiveBar.css';
import StudyHubTeamInbox from './StudyHubTeamInbox';
import PlanAwareLink from '../billing/PlanAwareLink';
import PlanTierLabel from '../billing/PlanTierLabel';
import { FEATURE } from '../../config/planFeatures';
import { useUserPlan } from '../../hooks/useUserPlan';
import { useLearningNext } from '../../features/adaptive-learning/useAdaptiveLearning';
import {
  actionTypeLabel,
  formatLearningReason,
  formatLearningTopicLabel,
  resolveLearningLink,
} from '../../features/adaptive-learning/adaptiveUtils';
import { getStudyDoNow, studyDoNowToNextAction } from '../hub/studyHubDoNow';
import '../../pages/adaptive/adaptive.css';
import './HomeDashboard.css';

const FLOW_ICONS = {
  Discover: LuCompass,
  Read: LuBookOpen,
  Save: LuBookmark,
  Practice: LuZap,
  Review: LuRefreshCw,
};

const NEXT_ACTION_ICONS = {
  REVIEW: LuRefreshCw,
  PRACTICE: LuZap,
  LEARN: LuBookOpen,
  QUIZ: LuListChecks,
  FLASHCARDS: LuBookmark,
  CHALLENGE: LuTarget,
  INTERVIEW: LuWandSparkles,
};

function NextActionTypeIcon({ type }) {
  const Icon = NEXT_ACTION_ICONS[type] || LuSparkles;
  return <Icon aria-hidden />;
}

function SectionBlock({ id, title, subtitle, children, action, icon: Icon }) {
  return (
    <section id={id} className="sh-block">
      <header className="sh-block-head">
        <div className="sh-block-head-main">
          {Icon && (
            <span className="sh-block-icon" aria-hidden>
              <Icon />
            </span>
          )}
          <div className="sh-block-head-copy">
            <h2 className="sh-block-title">{title}</h2>
            {subtitle && <p className="sh-block-sub">{subtitle}</p>}
          </div>
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

function QuestionList({ items, emptyMessage, priority }) {
  if (!items?.length) {
    return <p className="sh-empty">{emptyMessage}</p>;
  }
  return (
    <ul className={`sh-question-list${priority ? ' sh-question-list--priority' : ''}`}>
      {items.map((q) => (
        <li key={q.id}>
          <Link to={`/question/${q.id}`} className="sh-question-card">
            <span className="sh-question-card-icon" aria-hidden>
              <LuBookOpen />
            </span>
            <span className="sh-question-card-body">
              <span className="sh-question-topic">{q.topic}</span>
              <span className="sh-question-text">
                {String(q.question || '').slice(0, 140)}
                {String(q.question || '').length > 140 ? '…' : ''}
              </span>
            </span>
            <LuChevronRight className="sh-question-chevron" aria-hidden />
          </Link>
        </li>
      ))}
    </ul>
  );
}

function QuestionStrip({ title, subtitle, items, emptyMessage, priority, linkTo, linkLabel, icon }) {
  return (
    <SectionBlock
      title={title}
      subtitle={subtitle}
      icon={icon}
      action={
        linkTo ? (
          <Link to={linkTo} className="sh-block-link">
            {linkLabel}
          </Link>
        ) : null
      }
    >
      <QuestionList items={items} emptyMessage={emptyMessage} priority={priority} />
    </SectionBlock>
  );
}

const TOOL_LINKS = [
  { to: '/hub/today', label: "Today's plan", icon: LuTarget },
  { to: '/mastery', label: 'Knowledge map', icon: LuTrendingUp },
  { to: '/tutor', label: 'AI Tutor', icon: LuWandSparkles },
  { to: '/library', label: 'Library', icon: LuLibrary },
  { to: '/saved', label: 'Saved', icon: LuBookmark },
  { to: '/profile', label: 'Profile', icon: LuUser },
  { to: '/leaderboard', label: 'Leaderboard', icon: LuTrophy },
  { to: '/settings', label: 'Settings', icon: LuSettings },
];

export default function HomeDashboard() {
  const { apiClient } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [dashUpdatedAt, setDashUpdatedAt] = useState(null);
  const dailyGoal = getDailyReviewGoal();
  const realtime = useLearningRealtime();
  const { summary: gamificationSummary, loading: gamificationLoading, error: gamificationError } = useGamificationSummary();
  const { next: nextAction, loading: nextLoading, error: nextError, reload: reloadNext } = useLearningNext();
  const { canUseFeature } = useUserPlan();
  const leaderboardLocked = !canUseFeature(FEATURE.LEADERBOARD_RANK);

  const loadDashboard = useCallback(async (silent = false) => {
    try {
      const [dashRes, usageRes] = await Promise.all([
        apiClient.get('/api/home/dashboard', silent ? { timeout: 20000 } : undefined),
        apiClient.get('/api/users/me/usage').catch(() => ({ data: null })),
      ]);
      const dash = dashRes.data || {};
      if (usageRes?.data) {
        dash.stats = { ...(dash.stats || {}), ...usageRes.data };
      }
      setData(dash);
      setDashUpdatedAt(new Date());
      setError('');
    } catch {
      if (!silent) {
        setError(`${STUDY_HUB.name} unavailable — open the Library to keep studying.`);
      }
    }
  }, [apiClient]);

  useEffect(() => {
    loadDashboard(false);
  }, [loadDashboard]);

  useEffect(() => {
    const onInvalidate = () => loadDashboard(true);
    window.addEventListener(REALTIME_INVALIDATE_EVENT, onInvalidate);
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') loadDashboard(true);
    }, 60_000);
    return () => {
      window.clearInterval(id);
      window.removeEventListener(REALTIME_INVALIDATE_EVENT, onInvalidate);
    };
  }, [loadDashboard]);

  useEffect(() => {
    if (!realtime?.updatedAt) return;
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        stats: {
          ...(prev.stats || {}),
          dueCount: realtime.dueCount,
          savedCount: realtime.savedCount,
          reviewedToday: realtime.reviewedToday,
          streak: realtime.gamification?.currentStreak ?? prev.stats?.streak,
          gamificationLevel: realtime.gamification?.level ?? prev.stats?.gamificationLevel,
          lifetimeXp: realtime.gamification?.lifetimeXp ?? prev.stats?.lifetimeXp,
          learningScore: realtime.gamification?.learningScore ?? prev.stats?.learningScore,
          xpToNextLevel: realtime.gamification?.xpToNextLevel ?? prev.stats?.xpToNextLevel,
          levelProgressPercent: realtime.gamification?.levelProgressPercent ?? prev.stats?.levelProgressPercent,
          aiUsedToday: realtime.usage?.aiUsedToday ?? prev.stats?.aiUsedToday,
          aiDailyLimit: realtime.usage?.aiDailyLimit ?? prev.stats?.aiDailyLimit,
          aiRemaining: realtime.usage?.aiRemaining ?? prev.stats?.aiRemaining,
          plan: realtime.usage?.plan ?? prev.stats?.plan,
        },
      };
    });
  }, [realtime?.updatedAt, realtime?.dueCount, realtime?.savedCount, realtime?.reviewedToday, realtime?.gamification, realtime?.usage]);

  useEffect(() => {
    const scrollToHashSection = () => {
      const hash = window.location.hash;
      if (hash !== '#study-analytics') return;
      const id = hash.slice(1);
      window.setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    };
    scrollToHashSection();
    window.addEventListener('hashchange', scrollToHashSection);
    return () => window.removeEventListener('hashchange', scrollToHashSection);
  }, []);

  const stats = data?.stats || {};
  const reviewedToday = Number(stats.reviewedToday ?? realtime?.reviewedToday ?? 0);
  const due = Number(stats.dueCount ?? realtime?.dueCount ?? 0);
  const savedCount = Number(stats.savedCount ?? realtime?.savedCount ?? 0);
  const goalPct = dailyGoal ? Math.min(100, Math.round((reviewedToday / dailyGoal) * 100)) : 0;
  const aiPct = stats.aiDailyLimit
    ? Math.min(100, Math.round(((stats.aiUsedToday ?? 0) / stats.aiDailyLimit) * 100))
    : 0;
  const liveStamp = realtime?.updatedAt || dashUpdatedAt;
  const showUpgrade = stats.plan !== 'PRO' && stats.plan !== 'TEAM';

  const localNextAction = useMemo(
    () =>
      studyDoNowToNextAction(
        getStudyDoNow({ due, reviewedToday, dailyGoal, savedCount })
      ),
    [due, reviewedToday, dailyGoal, savedCount]
  );
  const displayNext = nextAction || localNextAction;
  const personalizingNext = nextLoading && !nextAction;
  const nextTopicRaw = displayNext?.topic?.name || displayNext?.topic?.slug || '';
  const nextTopicLabel = useMemo(() => formatLearningTopicLabel(nextTopicRaw), [nextTopicRaw]);
  const nextReasonText = useMemo(
    () => formatLearningReason(displayNext?.reason, nextTopicRaw),
    [displayNext?.reason, nextTopicRaw]
  );

  return (
    <div className="home-dashboard study-hub-v2" aria-label={STUDY_HUB.name}>
      <div className="sh-hero-grid">
        <LearningProgressPanel
          stats={stats}
          summary={gamificationSummary}
          loading={gamificationLoading}
          apiError={gamificationError}
          className="sh-progress-card"
        />

        <header className="sh-command">
          <div className="sh-command-top">
            <div className="sh-command-brand">
              <p className="sh-command-eyebrow">
                <LuGraduationCap aria-hidden />
                Learning hub
              </p>
              <div className="sh-command-title-row">
                <h1 className="sh-command-title">{STUDY_HUB.name}</h1>
                {stats.plan && (
                  <span className={`sh-plan-pill sh-plan-${String(stats.plan).toLowerCase()}`}>
                    {stats.plan}
                  </span>
                )}
              </div>
              <p className="sh-command-lead">{STUDY_HUB.tagline}</p>
            </div>
            {liveStamp && (
              <span className="sh-live-pill">
                <span className={`learning-live-dot ${realtime?.refreshing ? 'learning-live-dot--pulse' : ''}`} />
                Live · {formatLastSeen(liveStamp.toISOString())}
              </span>
            )}
          </div>

          <ol className="sh-flow" aria-label="Study flow">
            {STUDY_HUB.flowSteps.map((step, i) => {
              const StepIcon = FLOW_ICONS[step] || LuCompass;
              return (
                <li key={step}>
                  <span className="sh-flow-step">
                    <StepIcon aria-hidden />
                    {step}
                  </span>
                  {i < STUDY_HUB.flowSteps.length - 1 && <span className="sh-flow-sep" aria-hidden><LuChevronRight /></span>}
                </li>
              );
            })}
          </ol>

          <div className="sh-command-actions">
          <Link
            to="/review"
            className={`sh-action-tile sh-action-tile--review${due > 0 ? ' sh-action-tile--hot' : ''}`}
          >
            <span className="sh-action-tile-icon" aria-hidden>
              <LuRefreshCw />
            </span>
            <span className="sh-action-tile-text">
              <strong>Review</strong>
              <span>{due > 0 ? `${due} due now` : 'Queue clear'}</span>
            </span>
          </Link>
          <Link to="/practice" className="sh-action-tile sh-action-tile--practice">
            <span className="sh-action-tile-icon" aria-hidden>
              <LuZap />
            </span>
            <span className="sh-action-tile-text">
              <strong>Practice</strong>
              <span>Active recall</span>
            </span>
          </Link>
          <Link to="/hub" className="sh-action-tile sh-action-tile--learn">
            <span className="sh-action-tile-icon" aria-hidden>
              <LuBookOpen />
            </span>
            <span className="sh-action-tile-text">
              <strong>Learn</strong>
              <span>Notebooks &amp; PDFs</span>
            </span>
          </Link>
          {showUpgrade && (
            <Link to="/settings?tab=billing" className="sh-action-tile sh-action-tile--upgrade">
              <span className="sh-action-tile-icon" aria-hidden>
                <LuSparkles />
              </span>
              <span className="sh-action-tile-text">
                <strong>Upgrade</strong>
                <span>More AI &amp; Pro tools</span>
              </span>
            </Link>
          )}
          </div>
        </header>
      </div>

      {error && <p className="sh-alert" role="alert">{error}</p>}

      <section
        className="home-next-learn"
        aria-labelledby="home-next-learn-title"
        data-action-type={displayNext?.type ? String(displayNext.type).toLowerCase() : undefined}
      >
        {personalizingNext && (
          <p className="home-next-learn-status adaptive-muted">
            <LuSparkles aria-hidden />
            Personalizing your suggestion…
          </p>
        )}
        {nextError && !nextAction && (
          <p className="adaptive-muted home-next-learn-meta home-next-learn-status">
            {nextError}{' '}
            <button type="button" className="learn-btn learn-btn-secondary" onClick={reloadNext}>
              Retry
            </button>
          </p>
        )}
        {displayNext && (
          <div className="home-next-learn-layout">
            <div className="home-next-learn-icon" aria-hidden>
              <NextActionTypeIcon type={displayNext.type} />
            </div>
            <div className="home-next-learn-body">
              <header className="home-next-learn-head">
                <h2 id="home-next-learn-title">What should I learn now?</h2>
                {displayNext.type && (
                  <span
                    className={`action-badge action-badge--${String(displayNext.type).toLowerCase()}`}
                  >
                    {actionTypeLabel(displayNext.type)}
                  </span>
                )}
              </header>
              <p className="home-next-learn-reason">{nextReasonText}</p>
              <ul className="home-next-learn-meta-row" aria-label="Suggestion details">
                {nextTopicLabel && (
                  <li className="home-next-learn-chip">
                    <LuBookOpen aria-hidden />
                    <span>{nextTopicLabel}</span>
                  </li>
                )}
                {displayNext.estimatedMinutes != null && displayNext.estimatedMinutes > 0 && (
                  <li className="home-next-learn-chip">
                    <LuClock aria-hidden />
                    <span>~{displayNext.estimatedMinutes} min</span>
                  </li>
                )}
                {displayNext.questionCount != null && displayNext.questionCount > 0 && (
                  <li className="home-next-learn-chip">
                    <LuListChecks aria-hidden />
                    <span>{displayNext.questionCount} questions</span>
                  </li>
                )}
                {displayNext.localFallback && personalizingNext && (
                  <li className="home-next-learn-chip home-next-learn-chip--muted">
                    <LuSparkles aria-hidden />
                    <span>Quick pick</span>
                  </li>
                )}
              </ul>
              <div className="home-next-learn-actions">
                <Link
                  to={resolveLearningLink(displayNext)}
                  className="learn-btn learn-btn--primary home-next-learn-btn"
                >
                  <LuPlay aria-hidden />
                  Continue learning
                </Link>
                <Link to="/hub/today" className="learn-btn learn-btn-secondary home-next-learn-btn">
                  <LuCalendarDays aria-hidden />
                  Today&apos;s plan
                </Link>
                <Link to="/mastery" className="learn-btn learn-btn-secondary home-next-learn-btn">
                  <LuMap aria-hidden />
                  Knowledge map
                </Link>
                <Link to="/tutor" className="learn-btn learn-btn-secondary home-next-learn-btn">
                  <LuMessageCircle aria-hidden />
                  Ask tutor
                </Link>
                <Link
                  to="/interview"
                  className={`learn-btn learn-btn-secondary home-next-learn-btn${
                    displayNext?.type === 'INTERVIEW' ? ' home-next-learn-btn--emphasis' : ''
                  }`}
                >
                  <LuMic aria-hidden />
                  Mock interview
                </Link>
              </div>
            </div>
          </div>
        )}
      </section>

      <div className="sh-goals-panel">
        <div
          className="sh-goal-ring"
          style={{ '--sh-goal-pct': goalPct }}
          aria-hidden
        >
          <div className="sh-goal-ring-inner">
            <span className="sh-goal-ring-value">{reviewedToday}</span>
            <span className="sh-goal-ring-label">/ {dailyGoal}</span>
          </div>
        </div>
        <div className="sh-goals-copy">
          <p className="sh-goals-title">
            <LuTarget aria-hidden />
            Today&apos;s review goal
          </p>
          <p className="sh-goals-meta">
            {goalPct >= 100 ? 'Goal hit — nice work.' : `${dailyGoal - reviewedToday} left to hit your goal`}
          </p>
          <div className="sh-progress">
            <div className="sh-progress-fill sh-progress-fill--reviews" style={{ width: `${goalPct}%` }} />
          </div>
          {stats.aiDailyLimit != null && (
            <>
              <p className="sh-goals-ai">
                <LuSparkles aria-hidden />
                AI today · {stats.aiUsedToday ?? 0} / {stats.aiDailyLimit}
              </p>
              <div className="sh-progress sh-progress--thin">
                <div className="sh-progress-fill sh-progress-fill--ai" style={{ width: `${aiPct}%` }} />
              </div>
            </>
          )}
        </div>
        <div className="sh-metrics">
          <div className="sh-metric">
            <LuFlame className="sh-metric-icon" aria-hidden />
            <span className="sh-metric-value">{stats.streak ?? 0}</span>
            <span className="sh-metric-label">Day streak</span>
          </div>
          <div className="sh-metric">
            <LuEye className="sh-metric-icon" aria-hidden />
            <span className="sh-metric-value">{stats.readCount ?? 0}</span>
            <span className="sh-metric-label">Read</span>
          </div>
          <div className="sh-metric">
            <LuBookmark className="sh-metric-icon sh-metric-icon--saved" aria-hidden />
            <span className="sh-metric-value">{savedCount}</span>
            <span className="sh-metric-label">Saved</span>
          </div>
          <div className="sh-metric sh-metric--due">
            <LuAlarmClock className="sh-metric-icon sh-metric-icon--due" aria-hidden />
            <span className="sh-metric-value">{due}</span>
            <span className="sh-metric-label">Due</span>
          </div>
        </div>
      </div>

      <StudyHubAnalytics />

      <div className="sh-layout">
        <aside className="sh-rail" aria-label="Study tools">
          <p className="sh-rail-label">
            <LuLayoutGrid aria-hidden />
            Quick links
          </p>
          <nav className="sh-tool-nav">
            {TOOL_LINKS.map(({ to, label, icon: Icon }) => {
              const isLeaderboard = to === '/leaderboard';
              const LinkCmp = isLeaderboard && leaderboardLocked ? PlanAwareLink : Link;
              return (
                <LinkCmp key={to} to={to} className="sh-tool-link">
                  <Icon aria-hidden />
                  <span>{label}</span>
                  {isLeaderboard && leaderboardLocked && <PlanTierLabel tier="Pro" />}
                  {to === '/saved' && savedCount > 0 && (
                    <span className="sh-tool-badge">{savedCount}</span>
                  )}
                </LinkCmp>
              );
            })}
            <Link to="/settings?tab=billing" className="sh-tool-link sh-tool-link--muted">
              <LuSparkles aria-hidden />
              <span>Plan</span>
            </Link>
          </nav>
          <StudyHubTeamInbox placement="rail" />
          <div className="sh-rail-foot">
            <LuLibrary className="sh-rail-foot-icon" aria-hidden />
            <div>
              <span className="sh-rail-stat">{stats.libraryTotal ?? 0}</span>
              <span>questions in library</span>
            </div>
          </div>
        </aside>

        <div className="sh-main">
          {due > 0 && (
            <QuestionStrip
              title="Revision due"
              subtitle="Clear spaced-repetition items first"
              icon={LuAlarmClock}
              items={data?.revisionDue}
              emptyMessage=""
              priority
              linkTo="/review"
              linkLabel="Open queue"
            />
          )}

          <QuestionStrip
            title="Continue learning"
            subtitle="Recently touched questions"
            icon={LuBookOpen}
            items={data?.continueLearning}
            emptyMessage="Add catalog notebooks on Learn, then browse Library."
            linkTo="/library"
            linkLabel="Library"
          />

          <QuestionStrip
            title="Recommended"
            subtitle="Matched to your topics and history"
            icon={LuWandSparkles}
            items={data?.recommended}
            emptyMessage="Add notebooks from the catalog on Learn to get recommendations."
          />

          {data?.weakTopics?.length > 0 && (
            <SectionBlock title="Focus areas" subtitle="Topics with gaps in your reading" icon={LuTarget}>
              <div className="sh-tags">
                {data.weakTopics.map((t) => (
                  <span key={t} className="sh-tag">
                    <LuTarget aria-hidden />
                    {t}
                  </span>
                ))}
              </div>
            </SectionBlock>
          )}

          <QuestionStrip
            title="Trending"
            subtitle="Popular with learners this week"
            icon={LuTrendingUp}
            items={data?.trending}
            emptyMessage=""
          />
        </div>
      </div>

      <div className="sh-footer-help">
        <LearningGuide focus="hub" />
      </div>
    </div>
  );
}
